import React from "react";

import { useRouteError } from "react-router-dom";

import { MightyBotLogo } from "@mightybot/core-ui";
import { Button, Flex } from "@mightybot/web-ui";

import { isBuiltDomain, isPlatformDomain } from "../../utils/helpers";

import { ErrorFallbackContainer } from "./styled";

type ErrorFallbackProps = {
	errorType?: "notAdmin" | "routeNotFound" | "default";
};

export default function ErrorFallback({
	errorType = "default",
}: ErrorFallbackProps) {
	// This component is wired as React Router's `errorElement`. Router-caught
	// errors never reach window.onerror, so a route crash shows this page with a
	// completely silent console — no message, no stack, nothing to act on.
	//
	// console.* is NOT a usable channel here: webpack.config.js sets terser
	// `drop_console: true`, so every console call is compiled out of production
	// builds. Anything logged that way is invisible in exactly the environments
	// worth debugging. So publish the error on `window` instead, which survives
	// minification, and read it with `window.__MB_ROUTE_ERROR__`.
	//
	// `useRouteError` returns undefined when this component is rendered directly
	// (e.g. <ErrorFallback errorType="routeNotFound" />), so only publish when a
	// real route error is present.
	const routeError = useRouteError() as unknown;
	React.useEffect(() => {
		if (routeError === undefined || routeError === null) return;
		const detail =
			routeError instanceof Error
				? {
						name: routeError.name,
						message: routeError.message,
						stack: routeError.stack,
					}
				: { value: routeError };
		(window as unknown as Record<string, unknown>).__MB_ROUTE_ERROR__ = detail;
	}, [routeError]);

	const getErrorMessage = () => {
		switch (errorType) {
			case "notAdmin":
				return "You don't have permission to access this page.";
			case "routeNotFound":
				return "Oops! Page not found.";
			default:
				return "An unexpected error occurred.";
		}
	};

	const getSubMessage = () => {
		switch (errorType) {
			case "notAdmin":
				return "Please contact your administrator for access.";
			case "routeNotFound":
				return "This is not what you are looking for. Let's get you back to the app.";
			default:
				return "We're working on fixing this issue. Please try again later.";
		}
	};

	const getButtonText = () => {
		switch (errorType) {
			case "notAdmin":
				return "Go to Homepage";
			case "routeNotFound":
				return "Go to Homepage";
			default:
				return "Go to Homepage";
		}
	};

	const handleButtonClick = () => {
		// Built uses /app/dashboard, Platform uses /app/workflows, regular uses /app/settings/general
		const homePath = isBuiltDomain()
			? "/app/dashboard"
			: isPlatformDomain()
				? "/app/workflows"
				: "/app/settings/general";
		window.location.href = `${window.location.origin}${homePath}`;
	};

	return (
		<ErrorFallbackContainer>
			<Flex
				justify="center"
				align="center"
				style={{ height: "100vh", width: "60%" }}
			>
				<Flex
					justify="center"
					align="center"
					direction="column"
					style={{
						height: "400px",
						border: "1px solid #e0e0e0",
						borderRadius: "16px",
						width: "100%",
					}}
				>
					<Flex
						justify="between"
						align="center"
						direction="column"
						style={{ height: "100%" }}
					>
						<Flex
							style={{ padding: "40px", gap: "20px" }}
							direction="column"
							align="center"
							justify="center"
						>
							<img
								src={MightyBotLogo as string}
								alt="MightyBot Logo"
								style={{ width: "40px", height: "40px" }}
							/>
							<p style={{ fontSize: "22px", fontWeight: "500" }}>
								{getErrorMessage()}
							</p>
							<p style={{ fontSize: "14px", fontWeight: "400" }}>
								{getSubMessage()}
							</p>
							{/* @ts-ignore */}
							<Button
								onClick={handleButtonClick}
								style={{
									borderRadius: "22px",
									width: "300px",
									height: "44px",
									cursor: "pointer",
								}}
							>
								{getButtonText()}
							</Button>
							<p style={{ fontSize: "14px", fontWeight: "400" }}>
								If you face any issue, feel free to reach out to us via email at{" "}
								<a
									href="mailto:support@mightybot.ai"
									style={{ color: "var(--mb-blue-8)" }}
								>
									support@mightybot.ai
								</a>
								.
							</p>
						</Flex>
					</Flex>
					<Flex
						align="center"
						justify="center"
						style={{
							backgroundColor: "var(--gray-2)",
							width: "100%",
							borderRadius: "0px 0px 16px 16px",
							color: "var(--gray-9)",
							padding: "20px",
						}}
					>
						<p>© MightyBot. All rights reserved.</p>
					</Flex>
				</Flex>
			</Flex>
		</ErrorFallbackContainer>
	);
}
